import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { passwordService } from '../services/api';

const ForgotPassword: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await passwordService.forgotPassword(email.trim());
      setSent(true);
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(msg && typeof msg === 'string' ? msg : 'Erro ao processar solicitacao. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #002451, #0051A2, #007AF0)',
        p: 2,
      }}
    >
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 3, maxWidth: 384, width: '100%' }}>
        {branding.branding_logo ? (
          <Box
            component="img"
            src={branding.branding_logo}
            alt={branding.branding_nome}
            sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: '16px', mx: 'auto', mb: 2 }}
          />
        ) : (
          <Box
            sx={{
              width: 80, height: 80, mx: 'auto', mb: 2,
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(4px)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1.875rem', fontWeight: 700, color: 'white' }}>
              {(branding.branding_nome || 'NG').substring(0, 2).toUpperCase()}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '1.875rem', fontWeight: 700, color: 'white' }}>
          {branding.branding_nome || 'NeoGestor'}
        </Typography>
        <Typography sx={{ color: '#C5D4FF', mt: 0.5, fontSize: '0.875rem' }}>
          {branding.branding_subtitulo || 'Plataforma de Governanca de Indicadores'}
        </Typography>
      </Box>

      {/* Card */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%', maxWidth: 384,
          backgroundColor: '#fff', borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          p: 4,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#24252E', textAlign: 'center' }}>
          Recuperar senha
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3, textAlign: 'center' }}>
          {sent
            ? 'Verifique sua caixa de entrada.'
            : 'Informe seu e-mail para receber o link de recuperacao.'}
        </Typography>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 2, borderRadius: '8px',
              backgroundColor: '#FEECED', border: '1px solid #FAC8C9',
              fontSize: '0.875rem', color: '#A4151E',
            }}
          >
            {error}
          </Alert>
        )}

        {sent ? (
          <Alert
            severity="success"
            sx={{
              mb: 2, borderRadius: '8px',
              fontSize: '0.875rem',
            }}
          >
            Se o email existir no sistema, voce recebera um link para redefinir sua senha.
            Verifique tambem a pasta de spam.
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#515162', mb: 0.5 }}>
                E-mail
              </Typography>
              <TextField
                required
                fullWidth
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px', fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#D3D4DD' },
                    '&:hover fieldset': { borderColor: '#007AF0' },
                    '&.Mui-focused fieldset': { borderColor: '#007AF0' },
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.25, backgroundColor: '#007AF0',
                '&:hover': { boxShadow: 'none', backgroundColor: '#0069D0' },
                borderRadius: '8px', fontWeight: 500, fontSize: '0.875rem', boxShadow: 'none',
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar link de recuperacao'}
            </Button>
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/login')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              fontSize: '0.875rem', fontWeight: 500, color: '#007AF0',
              textDecoration: 'none',
              '&:hover': { color: '#0069D0', textDecoration: 'underline' },
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            Voltar ao login
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
